import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useGetUserQuery } from "../../../../store/auth/usersApiSlice";
import { useUpdateNotificationMutation } from "../../../../store/auth/usersApiSlice";
import { replaceClaimWithAccess } from "../../../../utils/Helper";
import { ReactComponent as RecentActivity } from "../../../../assets/icons/RecentActivity.svg";

/* Recent activities, as a timeline. */

/* Transcribed from Insights. */
const formatDate = (dateString) => {
  const date = new Date(dateString);
  const today = new Date();
  if (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  ) {
    return "Today";
  }
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "long",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

/* The design draws one glyph for every activity; the original varies it by
   type, so the variation is kept. */
const ACTIVITY_GLYPH = {
  payment: (
    <path d="M4 6a2 2 0 012-2h12a2 2 0 012 2v3a2 2 0 000 6v3a2 2 0 01-2 2H6a2 2 0 01-2-2v-3a2 2 0 000-6V6z" />
  ),
  request: <path d="M5 4h14v16H5zM8 8h8M8 12h8M8 16h5" />,
  order_request: <path d="M3 4h3l2 10h9l2-7H7M9 20h.01M17 20h.01" />,
  follow: <path d="M9 11a3 3 0 100-6 3 3 0 000 6zM3 20a6 6 0 0112 0M17 6a3 3 0 010 6" />,
};

const ActivityGlyph = ({ type }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {ACTIVITY_GLYPH[type] || (
      <>
        <path d="M4 6a2 2 0 012-2h12a2 2 0 012 2v3a2 2 0 000 6v3a2 2 0 01-2 2H6a2 2 0 01-2-2v-3a2 2 0 000-6V6z" />
        <path d="M12 7v10" strokeDasharray="1 2.5" />
      </>
    )}
  </svg>
);

export const RecentActivities = ({ notification_category }) => {
  const { userInfo, token } = useSelector((state) => state.auth);
  const [filteredData, setFilteredData] = useState([]);
  const { refetch } = useGetUserQuery(token);
  const [updateNotification] = useUpdateNotificationMutation();

  useEffect(() => {
    const notifications = userInfo?.notifications || [];
    setFilteredData(
      notification_category
        ? notifications.filter((n) => n.type === notification_category)
        : notifications
    );
  }, [userInfo?.notifications, notification_category]);

  /* Transcribed: marked read locally first, then on the server, rolling back
     if the request fails. */
  const handleNotificationClick = async (notificationId) => {
    const updated = filteredData.map((n) =>
      n.id === notificationId ? { ...n, is_read: true } : n
    );
    setFilteredData(updated);
    try {
      const response = await updateNotification({ notificationId, token }).unwrap();
      if (response) refetch();
    } catch (err) {
      console.error("Failed to update notification:", err);
      setFilteredData(filteredData);
    }
  };

  return (
    <section className="rounded-[18px] border border-[var(--line)] bg-white pb-[22px] pl-7 pr-3.5 pt-6 shadow-[0_1px_2px_rgba(20,40,60,.03)]">
      {/* Stays put as the list scrolls beneath it, as it does today. */}
      <h3 className="sticky top-0 z-[1] mb-3.5 bg-white pr-4 text-[19px] font-bold text-[var(--ink)] font-sans">
        Recent Activities
      </h3>

      {!filteredData.length ? (
        <div className="flex flex-col items-center justify-center py-7 pr-3.5">
          <RecentActivity />
          <span className="mt-5 text-center text-[14px] text-[var(--ink-2)] font-sans">
            No activities to show.
          </span>
        </div>
      ) : (
        <div className="u1-no-scrollbar max-h-[360px] overflow-y-auto pr-3">
          <div className="relative">
            {filteredData.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => handleNotificationClick(n.id)}
                className="u1-titem relative flex w-full gap-[15px] pb-[22px] text-left last:pb-0.5"
              >
                <span className="relative z-[1] flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full border-2 border-white bg-[var(--coral-soft)] text-[var(--coral)] shadow-[0_0_0_1px_var(--line)]">
                  <ActivityGlyph type={n.type} />
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block text-[14.5px] leading-[1.5] text-[var(--ink-2)] font-sans">
                    {replaceClaimWithAccess(n.message)}
                  </span>
                  <span className="mt-1.5 block text-[12.5px] text-[var(--muted-2)] font-sans">
                    {formatDate(n.created_at)}
                  </span>
                </span>

                {!n.is_read && (
                  <span
                    aria-hidden="true"
                    className="mt-2.5 h-[7px] w-[7px] shrink-0 rounded-full bg-[var(--coral)]"
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default RecentActivities;
